/**
 * Cards bundle: the brands-cards grid's modal.
 *
 * Every brand's details are server-rendered onto its card button as data-*
 * attributes, so opening the modal is a copy into the instance's single shell —
 * no AJAX, no REST. Content lands via textContent/src/href only, never
 * innerHTML, so nothing the attributes carry can become markup.
 */

/** Matches the modal's 0.2s CSS transition, with headroom for a late frame. */
const CLOSE_MS = 220;

function initCards(root: HTMLElement): void {
  const modal = root.querySelector<HTMLElement>(".top-brand-modal");
  const panel = root.querySelector<HTMLElement>(".top-brand-modal__panel");
  const closeButton = root.querySelector<HTMLButtonElement>(".top-brand-modal__close");
  const logo = root.querySelector<HTMLImageElement>(".top-brand-modal__logo");
  const initials = root.querySelector<HTMLElement>(".top-brand-modal__initials");
  const title = root.querySelector<HTMLElement>(".top-brand-modal__title");
  const desc = root.querySelector<HTMLElement>(".top-brand-modal__desc");
  const industryRow = root.querySelector<HTMLElement>(".top-brand-modal__industry-row");
  const industry = root.querySelector<HTMLElement>(".top-brand-modal__industry");
  const site = root.querySelector<HTMLAnchorElement>(".top-brand-modal__site");
  const siteLabel = root.querySelector<HTMLElement>(".top-brand-modal__site-label");

  if (
    !modal || !panel || !closeButton || !logo || !initials || !title ||
    !desc || !industryRow || !industry || !site || !siteLabel
  ) {
    return;
  }

  let invoker: HTMLElement | null = null;
  let closeTimer = 0;

  const open = (card: HTMLElement): void => {
    const data = card.dataset;

    title.textContent = data.title ?? "";
    desc.textContent = data.desc ?? "";

    // `hidden` drives every optional slot: logo vs initials, industry, website.
    logo.hidden = !data.logo;
    initials.hidden = Boolean(data.logo);
    if (data.logo) {
      logo.src = data.logo;
    }
    initials.textContent = data.initials ?? "";

    industryRow.hidden = !data.industry;
    industry.textContent = data.industry ?? "";

    site.hidden = !data.website;
    if (data.website) {
      site.href = data.website;
      siteLabel.textContent = data.websiteLabel || data.website;
    }

    invoker = card;
    window.clearTimeout(closeTimer);
    modal.hidden = false;
    // Double rAF: the browser must paint the un-hidden state once before
    // is-open lands, or the opacity/transform transition has nothing to run from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => modal.classList.add("is-open"));
    });
    document.body.classList.add("top-brand-modal-lock");
    closeButton.focus();
  };

  const close = (): void => {
    modal.classList.remove("is-open");
    document.body.classList.remove("top-brand-modal-lock");
    // A timeout rather than transitionend: reduced-motion zeroes the
    // transition, and transitionend would then never fire.
    closeTimer = window.setTimeout(() => {
      modal.hidden = true;
    }, CLOSE_MS);
    invoker?.focus();
    invoker = null;
  };

  root.querySelectorAll<HTMLElement>(".top-brand-tile").forEach((card) => {
    card.addEventListener("click", () => open(card));
  });

  closeButton.addEventListener("click", close);

  modal.addEventListener("click", (event) => {
    // Only a true backdrop hit closes: the panel carries its own padding, so
    // clicks anywhere inside it target the panel or a descendant, not the modal.
    if (event.target === modal) {
      close();
    }
  });

  // On document rather than the modal: a click inside the panel's text drops
  // focus to <body>, and a listener on the modal would stop hearing Escape.
  document.addEventListener("keydown", (event) => {
    if (modal.hidden) {
      return;
    }

    if (event.key === "Escape") {
      close();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    // Minimal trap — the modal only ever holds the close button and the
    // website link, so cycling two elements covers aria-modal's contract.
    const focusables = [closeButton, site].filter((el) => !el.hidden);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (!first || !last) {
      return;
    }

    if (!panel.contains(document.activeElement)) {
      // Focus fell out (backdrop mousedown, panel text click): pull it back in.
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

// Enqueued in the footer, so the shortcode markup is already parsed.
document.querySelectorAll<HTMLElement>(".top-brand-cards").forEach(initCards);

export {};
